import { useState, useEffect } from 'react';
import { useData } from '../hooks/useData';

const BASE = process.env.REACT_APP_API_URL || 'http://localhost:3001';

export default function AnalyticsPage({ refreshTrigger }) {
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);

  const { meetings: rawMeetings, actions: rawActions, loading: dataLoading } = useData(refreshTrigger);

  useEffect(() => {
    const meetings = rawMeetings;
    const actions = rawActions;
    if (dataLoading) return;
    (() => {
      const done = Array.isArray(meetings) ? meetings.filter(m => m.status === 'done') : [];
      const acts = Array.isArray(actions) ? actions : [];

      // Meetings per day last 14 days
      const days = {};
      for (let i = 13; i >= 0; i--) {
        const d = new Date(); d.setDate(d.getDate() - i);
        days[d.toLocaleDateString('en-US', { month: 'short', day: 'numeric' })] = 0;
      }
      done.forEach(m => {
        const label = new Date(m.created_at).toLocaleDateString('en-US', { month: 'short', day: 'numeric' });
        if (days[label] !== undefined) days[label]++;
      });

      // Owner leaderboard
      const ownerMap = {};
      acts.forEach(a => {
        if (!a.owner) return;
        if (!ownerMap[a.owner]) ownerMap[a.owner] = { total: 0, open: 0, done: 0 };
        ownerMap[a.owner].total++;
        if (a.status === 'done') ownerMap[a.owner].done++;
        else ownerMap[a.owner].open++;
      });
      const owners = Object.entries(ownerMap).map(([name, d]) => ({ name, ...d })).sort((a,b) => b.total - a.total).slice(0,8);

      // Priority breakdown
      const priorities = { high: 0, medium: 0, low: 0 };
      acts.forEach(a => { if (priorities[a.priority] !== undefined) priorities[a.priority]++; });

      // Action item status
      const totalActs = acts.length;
      const openActs = acts.filter(a => a.status !== 'done').length;
      const completionRate = totalActs ? Math.round(((totalActs - openActs) / totalActs) * 100) : 0;

      setData({ meetings: done, meetingsPerDay: days, owners, priorities, actions: acts, completionRate });
    })();
    setLoading(false);
  }, [rawMeetings, rawActions, dataLoading]);

  if (loading) return (
    <div className="page fade-in" style={{ display: 'flex', flexDirection: 'column', gap: '1rem' }}>
      {[80,160,120].map((h,i) => <div key={i} className="skeleton" style={{ height: h, borderRadius: 12 }} />)}
    </div>
  );

  if (!data || data.meetings.length === 0) return (
    <div className="page fade-in">
      <div style={{ marginBottom: '1.75rem' }}>
        <div style={{ fontSize: 24, fontWeight: 700, letterSpacing: '-0.02em', marginBottom: 4 }}>Analytics</div>
        <div style={{ fontSize: 13, color: 'var(--text-2)' }}>Meeting trends and team insights</div>
      </div>
      <div style={{ background: 'var(--bg-2)', border: '1px solid var(--border)', borderRadius: 12, padding: '5rem 2rem', textAlign: 'center' }}>
        <div style={{ fontSize: 44, marginBottom: '1rem' }}>📊</div>
        <div style={{ fontSize: 16, fontWeight: 600, color: 'var(--text-2)', marginBottom: 8 }}>No data yet</div>
        <div style={{ fontSize: 13, color: 'var(--text-3)' }}>Analyse some meetings first to unlock your insights</div>
      </div>
    </div>
  );

  const maxMPD = Math.max(...Object.values(data.meetingsPerDay), 1);
  const totalPriority = Object.values(data.priorities).reduce((a,b) => a+b, 0) || 1;
  const maxOwner = Math.max(...data.owners.map(o => o.total), 1);

  return (
    <div className="page fade-in">
      <div style={{ marginBottom: '1.75rem' }}>
        <div style={{ fontSize: 24, fontWeight: 700, letterSpacing: '-0.02em', marginBottom: 4 }}>Analytics</div>
        <div style={{ fontSize: 13, color: 'var(--text-2)' }}>Based on {data.meetings.length} analysed meeting{data.meetings.length !== 1 ? 's' : ''}</div>
      </div>

      {/* Top stats */}
      <div style={{ display: 'grid', gridTemplateColumns: 'repeat(4,1fr)', gap: '0.875rem', marginBottom: '1.25rem' }}>
        {[
          { val: data.meetings.length, label: 'Meetings Analysed', color: 'var(--accent)' },
          { val: data.actions.length, label: 'Action Items', color: 'var(--blue)' },
          { val: `${data.completionRate}%`, label: 'Completion Rate', color: data.completionRate > 70 ? 'var(--accent)' : 'var(--yellow)' },
          { val: data.owners.length, label: 'Team Members', color: 'var(--purple)' },
        ].map(s => (
          <div key={s.label} style={{ background: 'var(--bg-2)', border: '1px solid var(--border)', borderRadius: 10, padding: '1rem 1.25rem' }}>
            <div style={{ fontFamily: 'var(--mono)', fontSize: 28, fontWeight: 700, color: s.color, lineHeight: 1, marginBottom: 6 }}>{s.val}</div>
            <div style={{ fontSize: 12, fontWeight: 600, color: 'var(--text-2)' }}>{s.label}</div>
          </div>
        ))}
      </div>

      <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '1rem', marginBottom: '1rem' }}>

        {/* Meeting frequency */}
        <div style={{ background: 'var(--bg-2)', border: '1px solid var(--border)', borderRadius: 12, overflow: 'hidden' }}>
          <div style={{ padding: '0.875rem 1.25rem', borderBottom: '1px solid var(--border)' }}>
            <span style={{ fontFamily: 'var(--mono)', fontSize: 10, textTransform: 'uppercase', letterSpacing: '0.1em', color: 'var(--text-2)' }}>▦ Meeting Frequency — Last 14 Days</span>
          </div>
          <div style={{ padding: '1.25rem' }}>
            <div style={{ display: 'flex', alignItems: 'flex-end', gap: 4, height: 80, marginBottom: 8 }}>
              {Object.entries(data.meetingsPerDay).map(([label, count]) => (
                <div key={label} style={{ flex: 1, display: 'flex', flexDirection: 'column', alignItems: 'center' }} title={`${label}: ${count} meeting${count !== 1 ? 's' : ''}`}>
                  <div style={{
                    width: '100%', minHeight: 2,
                    height: `${Math.max((count / maxMPD) * 72, count > 0 ? 6 : 2)}px`,
                    background: count > 0 ? 'var(--accent)' : 'var(--border)',
                    borderRadius: '3px 3px 0 0', transition: 'height 0.4s ease',
                    opacity: count > 0 ? 1 : 0.4,
                  }} />
                </div>
              ))}
            </div>
            <div style={{ display: 'flex', justifyContent: 'space-between' }}>
              <span style={{ fontSize: 10, color: 'var(--text-3)', fontFamily: 'var(--mono)' }}>{Object.keys(data.meetingsPerDay)[0]}</span>
              <span style={{ fontSize: 10, color: 'var(--text-3)', fontFamily: 'var(--mono)' }}>{Object.keys(data.meetingsPerDay).at(-1)}</span>
            </div>
          </div>
        </div>

        {/* Priority breakdown */}
        <div style={{ background: 'var(--bg-2)', border: '1px solid var(--border)', borderRadius: 12, overflow: 'hidden' }}>
          <div style={{ padding: '0.875rem 1.25rem', borderBottom: '1px solid var(--border)' }}>
            <span style={{ fontFamily: 'var(--mono)', fontSize: 10, textTransform: 'uppercase', letterSpacing: '0.1em', color: 'var(--text-2)' }}>◎ Action Item Priority</span>
          </div>
          <div style={{ padding: '1.25rem', display: 'flex', flexDirection: 'column', gap: '1rem' }}>
            {[
              { label: 'High Priority', count: data.priorities.high, color: 'var(--red)' },
              { label: 'Medium Priority', count: data.priorities.medium, color: 'var(--yellow)' },
              { label: 'Low Priority', count: data.priorities.low, color: 'var(--text-3)' },
            ].map(p => (
              <div key={p.label}>
                <div style={{ display: 'flex', justifyContent: 'space-between', marginBottom: 6 }}>
                  <span style={{ fontSize: 13, fontWeight: 500 }}>{p.label}</span>
                  <span style={{ fontSize: 12, fontFamily: 'var(--mono)', color: 'var(--text-3)' }}>{p.count} ({Math.round((p.count/totalPriority)*100)}%)</span>
                </div>
                <div style={{ height: 8, background: 'var(--bg-4)', borderRadius: 4, overflow: 'hidden' }}>
                  <div style={{ height: '100%', width: `${(p.count/totalPriority)*100}%`, background: p.color, borderRadius: 4, transition: 'width 0.6s ease' }} />
                </div>
              </div>
            ))}
          </div>
        </div>
      </div>

      {/* Owner leaderboard */}
      {data.owners.length > 0 && (
        <div style={{ background: 'var(--bg-2)', border: '1px solid var(--border)', borderRadius: 12, overflow: 'hidden' }}>
          <div style={{ padding: '0.875rem 1.25rem', borderBottom: '1px solid var(--border)' }}>
            <span style={{ fontFamily: 'var(--mono)', fontSize: 10, textTransform: 'uppercase', letterSpacing: '0.1em', color: 'var(--text-2)' }}>👥 Action Item Owners</span>
          </div>
          <div style={{ padding: '1.25rem', display: 'flex', flexDirection: 'column', gap: '0.875rem' }}>
            {data.owners.map((o, i) => (
              <div key={o.name} style={{ display: 'flex', alignItems: 'center', gap: '1rem' }}>
                <span style={{ fontFamily: 'var(--mono)', fontSize: 11, color: 'var(--text-3)', width: 20, textAlign: 'right', flexShrink: 0 }}>{String(i+1).padStart(2,'0')}</span>
                <span style={{ fontSize: 13, fontWeight: 600, width: 100, flexShrink: 0 }}>{o.name}</span>
                <div style={{ flex: 1, height: 8, background: 'var(--bg-4)', borderRadius: 4, overflow: 'hidden' }}>
                  <div style={{ height: '100%', width: `${(o.total/maxOwner)*100}%`, background: 'linear-gradient(90deg, var(--accent), var(--blue))', borderRadius: 4, transition: 'width 0.6s ease' }} />
                </div>
                <div style={{ display: 'flex', gap: '0.5rem', alignItems: 'center', flexShrink: 0 }}>
                  <span style={{ fontSize: 11, fontFamily: 'var(--mono)', color: 'var(--accent)' }}>{o.done}✓</span>
                  <span style={{ fontSize: 11, fontFamily: 'var(--mono)', color: 'var(--text-3)' }}>{o.open} open</span>
                </div>
              </div>
            ))}
          </div>
        </div>
      )}
    </div>
  );
}
